// An activity on a phone, after SPhoneActivity: back to the space and Ask about this; its type, milestone and client
// request over the title; the facts; prep (a ring); then Steps (tick, numbered, with Next), Updates, Minutes or
// Files; and the assistant docked at the bottom: "Tell me a change to the steps". The page's own state (ticks laid
// over the server's copy while they save, the edit and remind sheets) stays in the Activity page; this draws it.
import { AddButton } from '../ui/AddButton';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import type { ActivityDetail, FileItem, SessionUser, Step } from '@dpw/shared';
import { ago, dayLabel, localDay, localTime } from '../lib/format';
import { AssistantAsks } from '../ui/ActivityAi';
import { ErrorNote, TypeChip } from '../ui/bits';
import { DrawnCheck } from '../ui/motion';
import { useToast } from '../ui/toast';
import { useAssistantPanel } from '../assistant/Panel';
import { P, PSheet, Ring, Seg, Tag, Title, TopBar } from './kit';
import { AddFileSheet, FileRows } from './files';
import { useRemember } from './recent';
import { SubActivities } from '../ui/SubActivities';

type Tab = 'steps' | 'updates' | 'minutes' | 'files';
const meeting = (a: ActivityDetail) => a.type === 'meeting' || a.type === 'workshop';

export function PhoneActivity({ a, me, files, canWrite, err, prepRisk, onToggle, onAddStep, onPost, onUpload, onEdit, onRemind, onDelete, onAddSub, sheet }: {
  a: ActivityDetail; me: SessionUser; files: FileItem[]; canWrite: boolean; err: unknown; prepRisk: boolean;
  onToggle: (st: Step) => void; onAddStep: (t: string, done: () => void) => void; onPost: (t: string, done: () => void) => void; onUpload: (f: File) => void;
  onEdit?: () => void; onRemind: () => void; onDelete?: () => void;
  /** A new sub-activity with all its details. */
  onAddSub?: () => void;
  /** In the activity sheet (6.2): its own top bar (Open full page, Ask AI, then More, then ×) in place of Back. */
  sheet?: { lead: ReactNode; close: ReactNode };
}) {
  const [params, setParams] = useSearchParams();
  const t = params.get('tab');
  const tab: Tab = t === 'updates' || t === 'minutes' || t === 'files' ? t : 'steps';
  const setTab = (x: Tab) => setParams(x === 'steps' ? {} : { tab: x }, { replace: true });
  const [step, setStep] = useState('');
  const [update, setUpdate] = useState('');
  const [adding, setAdding] = useState(false);
  const [more, setMore] = useState(false);
  const toast = useToast();
  useRemember({ to: `/activities/${a.id}`, title: a.title, sub: `${a.spaceName} · ${a.trackName}`, kind: 'activity' });
  const minutes = a.updates.filter(u => u.kind === 'minutes');
  const nextStep = a.steps.find(s => !s.done)?.id;
  const red = a.status === 'at_risk' || a.status === 'blocked' || a.isOverdue;
  const panel = useAssistantPanel();
  const ask = () => panel.open({ activityId: a.id, spaceId: a.spaceId, label: a.title });

  const facts: [string, React.ReactNode][] = [];
  if (meeting(a)) facts.push(['When', a.startsAt ? `${dayLabel(localDay(a.startsAt))} · ${localTime(a.startsAt)}${a.endsAt ? ` to ${localTime(a.endsAt)}` : ''}` : 'Not scheduled']);
  if (a.location) facts.push(['Where', a.location]);
  if (a.dueDate || !meeting(a)) facts.push(['Due', <span key="d" style={{ color: a.isOverdue ? 'var(--red-tx)' : undefined }}>{a.dueDate ? `${dayLabel(a.dueDate)}${a.dueTime ? ` ${a.dueTime}` : ''}` : 'No date'}</span>]);
  facts.push(['Track', <Link key="t" to={`/tracks/${a.trackId}`}>{a.trackName}</Link>]);
  facts.push([a.owners.length > 1 ? 'Owners' : 'Owner', a.owners.map(o => o.name.split(' ')[0]).join(', ') || 'None']);
  if (a.requestedBy) facts.push(['Requested by', a.requestedBy.name]);
  if (a.attendees.length) facts.push(['With', `${a.attendees.slice(0, 3).map(x => x.name).join(', ')}${a.attendees.length > 3 ? ` +${a.attendees.length - 3}` : ''}`]);
  if (a.waitingOn) facts.push(['Waiting on', `${a.waitingOn.name}${a.waitingSince ? ` · since ${ago(a.waitingSince)}` : ''}`]);
  if (a.partOf) facts.push(['Part of', <Link key="p" to={`/activities/${a.partOf.id}`}>{a.partOf.title}</Link>]);
  if (a.cameFrom) facts.push(['From', <Link key="c" to={`/activities/${a.cameFrom.id}`}>{a.cameFrom.title}</Link>]);

  const submitStep = (e: FormEvent) => { e.preventDefault(); const v = step.trim(); if (v) onAddStep(v, () => setStep('')); };
  const submitUpdate = (e: FormEvent) => { e.preventDefault(); const v = update.trim(); if (v) onPost(v, () => setUpdate('')); };

  return (
    <div className="ph-page">
      {sheet ? (
        <div className="ph-tbar actp-top">{sheet.lead}<button type="button" className="ph-nb" aria-label="More for this activity" onClick={() => setMore(true)}><P.more /></button>{sheet.close}</div>
      ) : (
        <TopBar back={{ to: `/spaces/${a.spaceId}`, label: a.spaceName }}>
          <button type="button" className="ph-nb" aria-label="More for this activity" onClick={() => setMore(true)}><P.more /></button>
          <button type="button" className="ph-nb" onClick={ask} aria-label="Ask about this"><P.spark /></button>
        </TopBar>
      )}
      <Title md title={a.title} tags={<>
        <TypeChip type={a.type} />
        {a.isMilestone && <span className="f13 mu" style={{ display: 'inline-flex', alignItems: 'center' }}><span className="ph-dia" />Milestone</span>}
        {a.isClientRequest && <span className="ph-clientl">Client request</span>}
        {red ? <Tag tone="r">{a.isOverdue ? 'Overdue' : a.status === 'blocked' ? 'Blocked' : 'At risk'}</Tag> : prepRisk ? <Tag tone="r">Prep at risk</Tag> : a.status === 'done' ? <Tag tone="g">Done</Tag> : null}
      </>} />
      <div className="ph-grp" aria-label="Facts">{facts.map(([k, v]) => <div className="ph-kv" key={k}><span>{k}</span><span>{v}</span></div>)}</div>
      {a.stepsTotal > 0 && <>
        <div className="gap16" />
        <div className="ph-card ph-prep">
          <Ring big pct={a.progress} color={a.progress >= 60 ? 'var(--grn)' : prepRisk || red ? 'var(--red)' : 'var(--amb)'}>{a.stepsDone}/{a.stepsTotal}</Ring>
          <div><div style={{ fontSize: 17, fontWeight: 600 }}>{a.needsPrep ? `Prep ${a.stepsDone} of ${a.stepsTotal} ready` : `${a.stepsDone} of ${a.stepsTotal} steps done`}</div>
            <div className="f13 mu" style={{ marginTop: 3 }}>{prepRisk ? 'At risk: less than half ready, and it is within three days.' : a.progress >= 100 ? 'Everything is ready.' : nextStep ? `Next: ${a.steps.find(s => s.id === nextStep)!.title}` : ''}</div></div>
        </div>
      </>}
      <AssistantAsks activityId={a.id} />
      <div className="gap16" />
      <Seg label="Activity" value={tab} onChange={setTab} options={[['steps', 'Steps'], ['updates', 'Updates'], ['minutes', 'Minutes'], ['files', `Files${files.length ? ` · ${files.length}` : ''}`]] as const} />
      <div className="gap16" />
      {err ? <ErrorNote error={err} /> : null}
      {tab === 'steps' && <>
        <div className="ph-grp" role="group" aria-label="Steps">
          {a.steps.map((st, i) => (
            <div className={`ph-rowx${st.done ? ' done' : ''}`} key={st.id} data-step={st.id}>
              <button type="button" className={`ph-ck${st.done ? ' on' : ''}`} aria-label={st.done ? `Mark not done: ${st.title}` : `Mark done: ${st.title}`} aria-pressed={st.done} disabled={!canWrite} onClick={() => onToggle(st)}>
                {st.done ? <DrawnCheck size={14} /> : <P.check />}
              </button>
              <span className="ph-rt"><div className="t"><span className="ph-num">{i + 1}</span>{st.title}</div>
                <div className="s">{[st.doneAt ? `Done ${ago(st.doneAt)}` : null, st.owner?.name.split(' ')[0]].filter(Boolean).join(' · ') || 'No owner'}</div></span>
              {st.done ? <Tag tone="g">Done</Tag> : st.id === nextStep ? <Tag tone="a">Next</Tag> : <Tag>To do</Tag>}
            </div>
          ))}
          {canWrite && (
            <form className="ph-qadd" onSubmit={submitStep}>
              <span className="ph-ck plus" aria-hidden="true"><P.plus /></span>
              <input value={step} onChange={e => setStep(e.target.value)} placeholder="Add a step" aria-label="Add a step" enterKeyHint="done" />
            </form>
          )}
        </div>
        <div className="gap16" />
        <SubActivities a={a} canWrite={canWrite} phone onAddFull={onAddSub} />
        {me.role !== 'partner' && <div className="ph-note" style={{ textAlign: 'left' }}>Change steps in plain words below: "add a step before 2: ...", "drop step 3", "step 2 is done".</div>}
      </>}
      {tab === 'updates' && <>
        {canWrite && (
          <form className="ph-ansb" onSubmit={submitUpdate} style={{ marginBottom: 16 }}>
            <textarea className="ph-inp" value={update} onChange={e => setUpdate(e.target.value)} placeholder="Post an update. Use @Name to tell someone." aria-label="Post an update" />
            <button className="ph-btf sec" disabled={!update.trim()}>Post update</button>
          </form>
        )}
        <div className="ph-grp" role="group" aria-label="Updates">
          {a.updates.map(u => <div className="ph-upd" key={u.id}><div className="w">{u.author?.name ?? 'Assistant'} · {ago(u.createdAt)}{u.kind === 'minutes' ? ' · minutes' : ''}</div><div className="x">{u.body}</div></div>)}
          {!a.updates.length && <div className="ph-rowx"><span className="ph-rt"><div className="s">No updates yet.</div></span></div>}
        </div>
      </>}
      {tab === 'minutes' && (
        <div className="ph-grp" role="group" aria-label="Minutes">
          {minutes.map(m => <div className="ph-upd" key={m.id}><div className="w">{m.author?.name ?? 'Assistant'} · {ago(m.createdAt)}{m.source === 'recording' ? ' · recorded in app' : ''}</div><div className="x">{m.body}</div></div>)}
          {!minutes.length && <div className="ph-rowx"><span className="ph-rt"><div className="t">No minutes yet</div><div className="s wrap">Minutes filed for this {a.type} show here.</div></span></div>}
        </div>
      )}
      {tab === 'files' && <>
        <FileRows files={files} />
        {canWrite && <><div className="gap16" /><AddButton className="wide" onClick={() => setAdding(true)}>Add file</AddButton></>}
        <div className="ph-note">Files here also show in the {a.trackName} track files.</div>
      </>}
      {me.role !== 'partner' && <Dock onSend={t => panel.open({ activityId: a.id, spaceId: a.spaceId, label: a.title, prompt: t, send: true })}
        onVoice={() => panel.open({ activityId: a.id, spaceId: a.spaceId, label: a.title, voice: true })} />}
      <AnimatePresence>
        {adding && <AddFileSheet key="add" title="Add to this activity" ask={`Add to ${a.title}: `} onPick={f => { onUpload(f); toast(`Adding ${f.name}`); }} onClose={() => setAdding(false)} />}
        {more && (
          <PSheet key="more" label={a.title} onClose={() => setMore(false)}>
            <h2>{a.title}</h2>
            {meeting(a) && <button type="button" className="ph-btf sec" onClick={() => { setMore(false); panel.open({ activityId: a.id, spaceId: a.spaceId, label: a.title, prompt: `Brief me for ${a.title}`, send: true }); }}>Brief me, 3 min</button>}
            {meeting(a) && <Link className="ph-btf sec" to={`/meetings/${a.id}`}>Minutes and recording</Link>}
            <button type="button" className="ph-btf sec" onClick={() => { setMore(false); onRemind(); }}>Remind me</button>
            {onEdit && <button type="button" className="ph-btf sec" onClick={() => { setMore(false); onEdit(); }}>Edit</button>}
            {onDelete && <button type="button" className="ph-btf sec" style={{ color: 'var(--red-tx)' }} onClick={() => { setMore(false); onDelete(); }}>Delete</button>}
            <button type="button" className="ph-btf ghost" onClick={() => setMore(false)}>Not now</button>
          </PSheet>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * The assistant at the bottom of the page: say a change to the steps. One AI panel everywhere (Kareem, 4 Nov): the words
 * go to the assistant panel looking at this activity, where the reply and its card show; the microphone opens it in
 * voice mode.
 */
function Dock({ onSend, onVoice }: { onSend: (t: string) => void; onVoice: () => void }) {
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => { e.preventDefault(); if (text.trim()) { onSend(text.trim()); setText(''); } };
  return (
    <div className="ph-dock">
      <form className="ph-abar" onSubmit={submit}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Tell me a change to the steps" aria-label="Tell the assistant a change" enterKeyHint="send" />
        <button type="button" className="mic" aria-label="Talk it through" onClick={onVoice}><P.mic /></button>
        <button className="go" aria-label="Send" disabled={!text.trim()}><P.send /></button>
      </form>
    </div>
  );
}
